import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';
import { COMPACT_DIALOG_PAPER_SX } from '@/components/shared/dialogStyles';

const FILTER_MESSAGE = `Each column can only be selected once. To search for multiple items in a single field,
 separate each search term with a comma.`;
const SORT_MESSAGE = `Sorting will be done in the priority listed below, starting with the first. Use the arrows
on the right to adjust the order of the list.`;

const BUTTON_NO_WRAP_SX = { whiteSpace: 'nowrap', flexShrink: 0, minWidth: 'fit-content', px: 2, lineHeight: 1.75 };

const CANCEL_BUTTON_SX = {
  ...BUTTON_NO_WRAP_SX,
  maxHeight: '36px',
  color: '#0c479d !important',
  backgroundColor: '#fff !important',
  boxShadow: 'none !important',
  transition: 'none !important',
  animation: 'none !important',
  '&:hover': {
    backgroundColor: '#fff !important',
    boxShadow: 'none !important',
  },
};

const APPLY_BUTTON_SX = {
  ...BUTTON_NO_WRAP_SX,
  maxHeight: '36px',
  letterSpacing: '0.08em',
  color: '#fff',
  backgroundColor: '#0c479d !important',
  border: 'none',
  boxShadow: 'none !important',
  transition: 'none !important',
  animation: 'none !important',
  '&:hover': {
    backgroundColor: '#0c479d !important',
    boxShadow: 'none !important',
  },
  '&.Mui-disabled': {
    backgroundColor: '#e0e0e0 !important',
    color: '#9e9e9e !important',
    boxShadow: 'none !important',
    border: 'none !important',
    cursor: 'not-allowed !important',
    pointerEvents: 'none',
    opacity: 1,
  },
};

const DIALOG_PAPER_SX = {
  ...COMPACT_DIALOG_PAPER_SX,
  backgroundColor: '#fff',
  p: 0,
};

export type SortFilterMode = 'sort' | 'filter';

export interface SortFilterOption {
  property: string;
  label: string;
}

export interface SortFilterValue extends SortFilterOption {
  value: string;
}

interface RowValue {
  property: string;
  value: string;
}

interface Props {
  open: boolean;
  mode: SortFilterMode;
  fields: SortFilterOption[];
  activeFields?: SortFilterValue[];
  title?: string;
  onClose: () => void;
  onApply: (items: SortFilterValue[]) => void;
}

const isSortMode = (mode: SortFilterMode) => mode === 'sort';

const getDefaultRow = (mode: SortFilterMode, fields: SortFilterOption[]): RowValue => ({
  property: fields[0]?.property ?? '',
  value: isSortMode(mode) ? 'DESC' : '',
});

export const SortFilterDialog: React.FC<Props> = ({
  open,
  mode,
  fields,
  activeFields = [],
  title,
  onClose,
  onApply,
}) => {
  const [rows, setRows] = useState<RowValue[]>([]);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;

    if (activeFields && activeFields.length > 0) {
      setRows(activeFields.map((item) => ({ property: item.property, value: item.value })));
    } else {
      setRows([]);
    }

    setSubmitted(false);
  }, [open, activeFields]);

  const propertyCounts = useMemo(() => {
    const counts = new Map<string, number>();
    rows.forEach((row) => {
      if (!row.property) return;
      counts.set(row.property, (counts.get(row.property) ?? 0) + 1);
    });
    return counts;
  }, [rows]);

  const hasDuplicate = (property: string) => !!property && (propertyCounts.get(property) ?? 0) > 1;

  const hasValidationErrors = rows.some((row) => !row.value || hasDuplicate(row.property));
  const canApplyEmptyState = activeFields.length > 0;
  const isInvalid = hasValidationErrors || (rows.length === 0 && !canApplyEmptyState);
  const isAddDisabled = rows.length > 0 && rows.length === fields.length;

  const dialogTitle = title ?? `${mode[0].toUpperCase() + mode.slice(1)} table columns`;
  const message = isSortMode(mode) ? SORT_MESSAGE : FILTER_MESSAGE;
  const applyLabel = `${mode.toUpperCase()}${rows.length > 1 ? 'S' : ''}`;

  const updateRow = (index: number, key: keyof RowValue, value: string) => {
    setRows((prev) => prev.map((row, rowIndex) => (rowIndex === index ? { ...row, [key]: value } : row)));
  };

  const addField = () => {
    setRows((prev) => [...prev, getDefaultRow(mode, fields)]);
  };

  const removeField = (index: number) => {
    setRows((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
  };

  const swap = (from: number, to: number) => {
    if (to < 0 || to >= rows.length) return;
    setRows((prev) => {
      const next = [...prev];
      [next[from], next[to]] = [next[to], next[from]];
      return next;
    });
  };

  const handleApply = () => {
    setSubmitted(true);
    if (isInvalid) return;

    const items: SortFilterValue[] = rows
      .filter((row) => row.property !== '' && row.value !== '')
      .map((row) => {
        const found = fields.find((field) => field.property === row.property);
        return {
          property: row.property,
          label: found?.label ?? row.property,
          value: row.value,
        };
      });

    onApply(items);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} slotProps={{ paper: { sx: DIALOG_PAPER_SX } }}>
      <DialogTitle
        className="title"
        sx={{
          px: 3,
          pt: 3,
          pb: '9px',
          color: 'rgba(0, 0, 0, .87)',
          fontFamily: 'Roboto, sans-serif',
          fontSize: '20px',
          fontWeight: 500,
          lineHeight: '32px',
          letterSpacing: '.0125em',
        }}
      >
        {dialogTitle}
      </DialogTitle>
      <Typography
        id="qa-sort-filter-message-lbl"
        sx={{
          px: 3,
          mt: 0,
          mb: 0,
          maxWidth: '522px',
          color: 'rgba(0, 0, 0, .54)',
          fontFamily: 'Roboto, sans-serif',
          fontSize: '16px',
          fontWeight: 400,
          lineHeight: '24px',
          letterSpacing: '.03125em',
        }}
      >
        {message}
      </Typography>

      <DialogContent sx={{ px: 3, pt: '20px', pb: '12px' }}>
        {rows.length > 0 && (
          <Box sx={{ maxHeight: 330, overflowY: 'auto', pt: 1, pb: 0.5 }}>
            {rows.map((row, index) => {
              const duplicateProperty = hasDuplicate(row.property);
              const showValueErrors = submitted;

              return (
                <Box key={`${row.property}-${index}`} sx={{ display: 'inline-block', width: '100%', mb: 1.5 }}>
                  {isSortMode(mode) && (
                    <Box
                      component="span"
                      sx={{
                        borderRadius: '5px',
                        backgroundColor: 'black',
                        fontWeight: 'bold',
                        px: '6px',
                        mr: '16px',
                        color: 'white',
                        fontSize: '18px',
                      }}
                    >
                      {index + 1}
                    </Box>
                  )}

                  <FormControl size="small" sx={{ width: '35%', mr: '16px' }} error={duplicateProperty}>
                    <InputLabel>{`Field to ${mode}`}</InputLabel>
                    <Select
                      id={`qa-field-sel-${index}`}
                      value={row.property}
                      label={`Field to ${mode}`}
                      onChange={(event: SelectChangeEvent) => updateRow(index, 'property', event.target.value)}
                    >
                      {fields.map((field) => (
                        <MenuItem key={field.property} value={field.property}>{field.label}</MenuItem>
                      ))}
                    </Select>
                    {duplicateProperty && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        Cannot have duplicate values.
                      </Typography>
                    )}
                  </FormControl>

                  {!isSortMode(mode) ? (
                    <TextField
                      id={`qa-keywordFilter-ipt-${index}`}
                      size="small"
                      label="Keyword filter"
                      value={row.value}
                      onChange={(event) => updateRow(index, 'value', event.target.value)}
                      sx={{ width: '35%', mr: '16px' }}
                      error={showValueErrors && !row.value}
                      helperText={showValueErrors && !row.value ? 'Must enter a value.' : ' '}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') event.preventDefault();
                      }}
                    />
                  ) : (
                    <FormControl size="small" sx={{ width: '35%', mr: '16px' }}>
                      <InputLabel>Sort direction</InputLabel>
                      <Select
                        id={`qa-keywordSort-sel-${index}`}
                        value={row.value}
                        label="Sort direction"
                        onChange={(event: SelectChangeEvent) => updateRow(index, 'value', event.target.value)}
                      >
                        <MenuItem value="ASC">Ascending (A-Z, 0-9)</MenuItem>
                        <MenuItem value="DESC">Descending (Z-A, 9-0)</MenuItem>
                      </Select>
                    </FormControl>
                  )}

                  {isSortMode(mode) && (
                    <>
                      <IconButton
                        id={`qa-moveDown-btn-${index}`}
                        onClick={() => swap(index, index + 1)}
                        disabled={index + 1 === rows.length}
                        sx={{ p: 0, mr: '4px', color: '#0c479d', '&:disabled': { color: '#bdbdbd' } }}
                      >
                        <ArrowDownwardIcon />
                      </IconButton>
                      <IconButton
                        id={`qa-moveUp-btn-${index}`}
                        onClick={() => swap(index, index - 1)}
                        disabled={index === 0}
                        sx={{ p: 0, mr: '4px', color: '#0c479d', '&:disabled': { color: '#bdbdbd' } }}
                      >
                        <ArrowUpwardIcon />
                      </IconButton>
                    </>
                  )}

                  <IconButton
                    id={`qa-deleteFilter-btn-${index}`}
                    onClick={() => removeField(index)}
                    sx={{ p: 0, mr: '4px', color: 'rgba(0, 0, 0, .6)' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pt: '8px', pb: 3, mb: 0, display: 'block' }}>
        <Box sx={{ flexBasis: '100%', mb: 0, px: 0 }}>
          <Button
            id="qa-addFilter-btn"
            variant="text"
            startIcon={<AddIcon />}
            onClick={addField}
            disabled={isAddDisabled || fields.length === 0}
            sx={{ border: 'none', color: '#0c479d', whiteSpace: 'nowrap', ml: '-8px' }}
          >
            {`ADD ${mode.toUpperCase()}`}
          </Button>
        </Box>

        <Box sx={{ px: 0, pb: 0, display: 'flex', flexWrap: 'nowrap', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
          <Button id="qa-cancel-btn" onClick={onClose} sx={CANCEL_BUTTON_SX}>CANCEL</Button>
          <Button
            id="qa-applyFilters-btn"
            variant="contained"
            onClick={handleApply}
            disabled={isInvalid}
            sx={APPLY_BUTTON_SX}
          >
            {`APPLY ${applyLabel}`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
